/**
 * MappingApprovalQueue — Step 3 (Data Mapping) AI-validation panel.
 *
 * Mapping approval is now fully automatic: an AI critic validates and approves
 * every DataMapping in the background. This component is read-only — it renders
 * all DataMapping nodes for a case with a per-row AI verdict badge
 * (AI-validated / AI flagged / AI rejected) and a header strip summarising
 * validation progress ("23 / 23 validated · 2 flagged").
 *
 * It fires an `onValidationChange` callback whenever the computed snapshot
 * changes — Step3DataMapping uses this to unlock the "Continue to Schema
 * Design" button once background validation is complete. There are no
 * approve/reject controls; the consultant never approves mappings manually.
 *
 * Empty state: when the case has no mappings yet (Step 3 hasn't generated
 * them), renders a small CTA pointing at the Generate Mappings button that
 * already lives in Step 3.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ListChecks,
} from 'lucide-react';
import { Card, CardBody } from '../ui/Card';
import { caseAnalysisAPI, type DataMappingRecord } from '../../lib/api';

export interface MappingApprovalQueueProps {
  caseId: string | null | undefined;
  /**
   * Fires whenever the computed validation snapshot changes. Step3DataMapping
   * uses this to drive its "Continue" button's disabled state. Receives the
   * full snapshot so callers can show their own messaging without duplicating
   * math.
   */
  onValidationChange?: (snapshot: ValidationSnapshot) => void;
}

export interface ValidationSnapshot {
  total: number;
  aiValidated: number;
  flagged: number;
  /** True once every mapping has been AI-validated (or there are none). */
  validationComplete: boolean;
}

const STALE_30_SEC = 1000 * 30;

/** Map a mapping's AI verdict (falling back to legacy approved flag) to a label. */
function deriveVerdict(m: DataMappingRecord): 'approve' | 'flag' | 'reject' {
  const verdict = (m.validationVerdict || '').toLowerCase();
  if (verdict === 'flag' || verdict === 'reject' || verdict === 'approve') {
    return verdict as 'approve' | 'flag' | 'reject';
  }
  // Legacy mappings without a verdict: treat approved as validated.
  return m.approved ? 'approve' : 'flag';
}

export default function MappingApprovalQueue({
  caseId,
  onValidationChange,
}: MappingApprovalQueueProps) {
  const [expandedMappingId, setExpandedMappingId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<DataMappingRecord[]>({
    queryKey: ['case-analysis', 'mappings', caseId],
    queryFn: () => caseAnalysisAPI.listDataMappings(caseId!),
    enabled: !!caseId,
    staleTime: STALE_30_SEC,
    retry: false,
  });

  const mappings = useMemo(() => data ?? [], [data]);

  const snapshot: ValidationSnapshot = useMemo(() => {
    const total = mappings.length;
    // A mapping counts as AI-validated if it carries the flag, or (legacy)
    // if it's approved — so older cases report as complete and don't block.
    const aiValidated = mappings.filter(
      (m) => m.aiValidated || m.approved,
    ).length;
    const flagged = mappings.filter(
      (m) => deriveVerdict(m) === 'flag' || deriveVerdict(m) === 'reject',
    ).length;
    return {
      total,
      aiValidated,
      flagged,
      validationComplete: total === 0 || aiValidated >= total,
    };
  }, [mappings]);

  // Notify parent whenever the snapshot changes.
  useEffect(() => {
    onValidationChange?.(snapshot);
  }, [snapshot, onValidationChange]);

  if (!caseId) return null;

  if (isLoading) {
    // Skeleton mirrors the loaded layout: a validation-summary header strip on
    // top plus 4 placeholder mapping rows.
    return (
      <div className="space-y-3 animate-pulse" aria-busy="true" aria-live="polite">
        <div className="p-4 rounded-lg border border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="h-3.5 w-32 bg-neutral-200 rounded" />
            <div className="h-3.5 w-28 bg-neutral-200 rounded" />
          </div>
          <div className="w-full h-1.5 bg-white/60 rounded-full overflow-hidden">
            <div className="h-full w-1/3 bg-neutral-200 rounded-full" />
          </div>
          <div className="h-2.5 w-3/5 bg-neutral-200 rounded mt-2" />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <Card key={i} className="p-3 border border-neutral-200">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0 space-y-2">
                <div className="h-3.5 w-3/4 bg-neutral-200 rounded" />
                <div className="h-2.5 w-1/2 bg-neutral-100 rounded" />
              </div>
              <div className="h-6 w-24 bg-neutral-200 rounded-full shrink-0" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <p className="text-sm">
          Could not load mappings.{' '}
          <span className="text-xs">{(error as Error).message}</span>
        </p>
      </div>
    );
  }

  if (mappings.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="flex flex-col items-center gap-2 text-neutral-600 py-8 text-center">
            <ListChecks className="w-8 h-8 text-neutral-400" />
            <p className="text-sm font-medium text-neutral-700">No data mappings yet</p>
            <p className="text-xs text-neutral-500 max-w-md">
              Generate mappings for this case (above) — the AI will validate and
              approve them automatically.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  const validatedPct =
    snapshot.total > 0 ? Math.round((snapshot.aiValidated / snapshot.total) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Header strip — AI validation summary + progress bar */}
      <div
        className={`p-4 rounded-lg border ${
          snapshot.validationComplete
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-amber-50 border-amber-200'
        }`}
      >
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <ListChecks
              className={`w-4 h-4 ${
                snapshot.validationComplete ? 'text-emerald-700' : 'text-amber-700'
              }`}
            />
            <span
              className={`text-sm font-semibold ${
                snapshot.validationComplete ? 'text-emerald-900' : 'text-amber-900'
              }`}
            >
              AI Validation
            </span>
          </div>
          <span
            className={`text-sm font-medium ${
              snapshot.validationComplete ? 'text-emerald-700' : 'text-amber-700'
            }`}
          >
            {snapshot.aiValidated} / {snapshot.total} validated
            {snapshot.flagged > 0 ? ` · ${snapshot.flagged} flagged` : ''}
          </span>
        </div>
        <div className="w-full h-1.5 bg-white/60 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              snapshot.validationComplete ? 'bg-emerald-500' : 'bg-amber-500'
            }`}
            style={{ width: `${validatedPct}%` }}
          />
        </div>
        <p
          className={`text-xs mt-1.5 ${
            snapshot.validationComplete ? 'text-emerald-700' : 'text-amber-700'
          }`}
        >
          {snapshot.validationComplete
            ? 'AI validation complete. Schema design is unlocked.'
            : 'AI is validating mappings…'}
        </p>
      </div>

      {/* Mapping rows — read-only verdict badges */}
      <div className="space-y-2">
        {mappings.map((mapping) => {
          const isExpanded = expandedMappingId === mapping.mappingId;
          const verdict = deriveVerdict(mapping);
          const rationale = mapping.validationRationale || mapping.rationale;
          const confidencePct =
            mapping.confidence != null ? Math.round(mapping.confidence * 100) : null;

          return (
            <Card
              key={mapping.mappingId}
              className={`p-3 transition-colors ${
                verdict === 'reject'
                  ? 'border border-red-200 bg-red-50/30'
                  : verdict === 'flag'
                  ? 'border border-amber-200 bg-amber-50/30'
                  : 'border border-emerald-200 bg-emerald-50/40'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-neutral-900 truncate">
                      {mapping.sourceSystem || '—'}
                      <span className="text-neutral-400 mx-1">·</span>
                      {mapping.sourceTable || '—'}
                      <span className="text-neutral-400 mx-1">.</span>
                      <span className="text-primary-700">{mapping.sourceColumn || '—'}</span>
                    </span>
                    <span className="text-xs text-neutral-400">→</span>
                    <span className="text-sm font-medium text-neutral-700">
                      {mapping.targetEntity || '—'}
                      <span className="text-neutral-400 mx-1">.</span>
                      <span className="text-neutral-900">
                        {mapping.targetProperty ||
                          mapping.targetProperties?.[0] ||
                          '—'}
                      </span>
                    </span>
                    {confidencePct != null && (
                      <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                          mapping.confidenceLevel === 'HIGH' || confidencePct >= 85
                            ? 'bg-emerald-100 text-emerald-700'
                            : mapping.confidenceLevel === 'MEDIUM' || confidencePct >= 60
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-neutral-100 text-neutral-600'
                        }`}
                      >
                        {confidencePct}% conf.
                      </span>
                    )}
                  </div>
                  {rationale && (
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedMappingId(isExpanded ? null : mapping.mappingId)
                      }
                      className="mt-1 flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="w-3 h-3" />
                          Hide AI rationale
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3 h-3" />
                          View AI rationale
                        </>
                      )}
                    </button>
                  )}
                  {isExpanded && rationale && (
                    <p className="text-xs text-neutral-600 mt-1.5 leading-relaxed">
                      {rationale}
                    </p>
                  )}
                </div>

                {/* Read-only AI verdict badge */}
                <div className="shrink-0">
                  {verdict === 'reject' ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border border-red-300 text-red-700 bg-red-50">
                      <XCircle className="w-3.5 h-3.5" />
                      AI rejected (review)
                    </span>
                  ) : verdict === 'flag' ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border border-amber-300 text-amber-700 bg-amber-50">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      AI flagged
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      AI-validated
                    </span>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
